import React, { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { PageHeader, EmptyState, Chip, TableLoadingSkeleton } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { MagnifyingGlass, DownloadSimple, Eye } from "@phosphor-icons/react";
import { toast } from "sonner";

const inrFull = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(n || 0);
const MODE_LABEL = { cash: "Cash", cheque: "Cheque", upi: "UPI", bank_transfer: "Bank Transfer", online: "Online", other: "Other" };

export default function MyReceipts() {
    const [receipts, setReceipts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [preview, setPreview] = useState({ open: false, receipt: null });

    async function load() {
        setLoading(true);
        try {
            // Backend auto-scopes to the resident's flat.
            const res = await api.get("/receipts", { params: q ? { q } : {} });
            setReceipts(res.data);
        } catch (e) { toast.error(formatError(e)); }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
    useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

    async function downloadPdf(r) {
        try {
            const resp = await api.get(`/receipts/${r.id}/pdf`, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = `${r.receipt_number}-${r.flat_label || "receipt"}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) { toast.error(formatError(e)); }
    }

    const totalPaid = receipts.reduce((s, r) => s + (r.amount || 0), 0);

    return (
        <div data-testid="my-receipts-page">
            <PageHeader
                overline="Finance"
                title="My Receipts"
                description="Every payment you have made — download proof any time."
            />

            <div className="bg-white border border-brand-line rounded-sm p-4 mb-5 flex flex-wrap gap-4 items-center justify-between">
                <div>
                    <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft">Total paid across receipts</p>
                    <p className="font-heading text-2xl text-brand-ink mt-1" data-testid="myrcpt-total">{inrFull(totalPaid)}</p>
                </div>
                <div className="text-sm text-brand-inkSoft">
                    {receipts.length} receipt{receipts.length === 1 ? "" : "s"}
                </div>
            </div>

            <div className="bg-white border border-brand-line rounded-sm p-3 mb-4">
                <Label className="text-[10px] uppercase tracking-overline text-brand-inkSoft">Search</Label>
                <div className="relative mt-1 max-w-md">
                    <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-brand-inkSoft" />
                    <Input data-testid="myrcpt-q" value={q} onChange={e => setQ(e.target.value)} placeholder="Receipt no. / invoice / period" className="pl-7 rounded-sm border-brand-line h-9" />
                </div>
            </div>

            {loading ? <TableLoadingSkeleton rows={4} /> : receipts.length === 0 ? (
                <EmptyState
                    title="No receipts yet"
                    description="Once your maintenance invoice is fully paid, a receipt appears here automatically. You'll be able to view or download it any time."
                />
            ) : (
                <div className="bg-white border border-brand-line rounded-sm overflow-hidden">
                    <table className="w-full text-sm" data-testid="myrcpt-table">
                        <thead className="bg-brand-bg">
                            <tr className="text-[10px] uppercase tracking-overline text-brand-inkSoft">
                                <th className="px-3 py-2 text-left">Receipt No.</th>
                                <th className="px-3 py-2 text-left">Date</th>
                                <th className="px-3 py-2 text-left">Invoice No.</th>
                                <th className="px-3 py-2 text-left">Period</th>
                                <th className="px-3 py-2 text-left">Mode</th>
                                <th className="px-3 py-2 text-right">Amount</th>
                                <th className="px-3 py-2"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {receipts.map(r => (
                                <tr key={r.id} className="border-t border-brand-line hover:bg-brand-bg/40" data-testid={`myrcpt-row-${r.id}`}>
                                    <td className="px-3 py-2 font-mono text-xs text-brand-ink">{r.receipt_number}</td>
                                    <td className="px-3 py-2 text-brand-inkSoft">{(r.created_at || "").slice(0, 10)}</td>
                                    <td className="px-3 py-2 font-mono text-xs text-brand-ink">{r.invoice_ref || "—"}</td>
                                    <td className="px-3 py-2 text-brand-inkSoft">{r.invoice_month || r.invoice_description || "—"}</td>
                                    <td className="px-3 py-2">{MODE_LABEL[r.payment_mode] || "—"}</td>
                                    <td className="px-3 py-2 text-right font-medium">{inrFull(r.amount)}</td>
                                    <td className="px-3 py-2 text-right whitespace-nowrap">
                                        <button onClick={() => setPreview({ open: true, receipt: r })} className="p-1.5 text-brand-inkSoft hover:text-brand-action" title="View" data-testid={`myrcpt-view-${r.id}`}><Eye size={14} /></button>
                                        <button onClick={() => downloadPdf(r)} className="p-1.5 text-brand-inkSoft hover:text-brand-action" title="Download PDF" data-testid={`myrcpt-pdf-${r.id}`}><DownloadSimple size={14} /></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <PreviewDialog receipt={preview.receipt} open={preview.open} onClose={() => setPreview({ open: false, receipt: null })} onDownload={downloadPdf} />
        </div>
    );
}

function PreviewDialog({ receipt, open, onClose, onDownload }) {
    if (!receipt) return null;
    const snap = receipt.snapshot || {};
    const charges = receipt.invoice_charges || [];
    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
            <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="myrcpt-preview">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl tracking-tight">Payment receipt</DialogTitle>
                    <DialogDescription>{receipt.receipt_number}</DialogDescription>
                </DialogHeader>
                <div className="bg-white border border-brand-line rounded-sm p-6 space-y-5">
                    <div className="flex items-start justify-between">
                        <div>
                            <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft">Payment Receipt</p>
                            <p className="font-heading text-xl text-brand-action mt-1">{receipt.receipt_number}</p>
                            <p className="text-xs text-brand-inkSoft mt-1">Issued {(receipt.created_at || "").slice(0, 10)}</p>
                        </div>
                        <Chip variant="success">PAID</Chip>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Received from" value={snap.resident_name || "—"} />
                        <Field label="Flat" value={receipt.flat_label || snap.flat_label || "—"} />
                        <Field label="Invoice No." value={receipt.invoice_ref || "—"} />
                        <Field label="Invoice period" value={receipt.invoice_month || receipt.invoice_description || "—"} />
                        <Field label="Payment date" value={receipt.payment_date || "—"} />
                        <Field label="Payment mode" value={MODE_LABEL[receipt.payment_mode] || "—"} />
                        <Field label="Amount received" value={inrFull(receipt.amount)} strong />
                    </div>
                    {receipt.transaction_reference && (
                        <div className="text-sm border-t border-brand-line pt-3">
                            <span className="text-[10px] uppercase tracking-overline text-brand-inkSoft">Reference</span>
                            <div className="text-brand-ink">{receipt.transaction_reference}</div>
                        </div>
                    )}
                    {charges.length > 0 && (
                        <div className="border-t border-brand-line pt-3">
                            <span className="text-[10px] uppercase tracking-overline text-brand-inkSoft">Charges</span>
                            <table className="w-full text-sm mt-1">
                                <tbody>
                                    {charges.map((c, i) => (
                                        <tr key={i} className="border-t border-brand-line/60">
                                            <td className="py-1.5">{c.name}</td>
                                            <td className="py-1.5 text-right">{inrFull(c.amount)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                    <div className="flex justify-between items-center bg-[#33383F] text-white rounded-sm px-4 py-3">
                        <span className="text-sm">Total received</span>
                        <span className="font-heading text-lg">{inrFull(receipt.amount)}</span>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" className="rounded-full" onClick={onClose}>Close</Button>
                    <Button className="rounded-full" onClick={() => onDownload(receipt)} data-testid="myrcpt-preview-download">
                        <DownloadSimple size={14} className="mr-1" /> Download PDF
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function Field({ label, value, strong }) {
    return (
        <div>
            <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft">{label}</p>
            <p className={`text-sm ${strong ? "font-heading text-lg text-brand-ink" : "text-brand-ink"}`}>{value}</p>
        </div>
    );
}
